export type InvoiceStatus =
  | "DRAFT"
  | "ISSUED"
  | "SENT"
  | "VIEWED"
  | "PARTIALLY_PAID"
  | "PAID"
  | "OVERDUE"
  | "VOID";

export type PaymentMethod = "CHECK" | "ACH" | "CARD" | "WIRE" | "OTHER";

export type ReminderKind = "DUE_SOON" | "OVERDUE";

export interface LineItem {
  description: string;
  hours?: number;
  rate: number;
  amount: number;
}

export interface Invoice {
  id: string;
  // Internal assignee — optional since 046. Older invoices have an
  // employee_id; new invoices may have client_id instead (or both).
  employee_id?: string | null;
  employee_name?: string | null;
  // Recipient (external client). All four are nullable: a draft invoice
  // may have none of them filled in yet.
  client_id?: string | null;
  client_name?: string | null;
  client_company?: string | null;
  client_email?: string | null;
  // The actual address used at send time: client_email > saved client.email
  // > sent_to_email. The UI renders this in the list view.
  recipient_email?: string | null;
  invoice_number: string;
  period_start: string;
  period_end: string;
  line_items_json: LineItem[];
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  amount_paid?: number;
  balance_due?: number;
  currency: string;
  fx_rate_to_base?: number;
  total_in_base?: number;
  balance_due_in_base?: number;
  status: InvoiceStatus;
  due_date?: string;
  issued_at?: string;
  sent_at?: string;
  viewed_at?: string;
  paid_at?: string;
  voided_at?: string;
  void_reason?: string;
  pdf_url?: string;
  sent_to_email?: string;
  terms?: string;
  notes?: string;
  created_at: string;
}

// ─────────── Invoice clients (recipient address book) ───────────

export interface InvoiceClient {
  id: string;
  org_id: string;
  name: string;
  email: string;
  company?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface InvoiceClientCreate {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  address?: string;
  notes?: string;
}

export type InvoiceClientUpdate = Partial<InvoiceClientCreate> & {
  is_archived?: boolean;
};

export interface InvoiceClientRollup {
  invoice_count: number;
  total_billed: number;
  total_paid: number;
  total_outstanding: number;
  by_status: Partial<Record<InvoiceStatus, number>>;
}

export interface InvoiceClientWithRollup {
  client: InvoiceClient;
  rollup: InvoiceClientRollup;
}

/** Lightweight invoice row used by the per-client history panel. */
export interface InvoiceClientInvoiceRow {
  id: string;
  invoice_number: string;
  status: InvoiceStatus;
  total: number;
  amount_paid: number;
  balance_due: number;
  currency: string;
  due_date?: string;
  issued_at?: string;
  sent_at?: string;
  paid_at?: string;
  created_at: string;
}

export interface Payment {
  id: string;
  invoice_id?: string;
  amount: number;
  method: PaymentMethod;
  reference?: string;
  received_at: string;
  notes?: string;
  created_at?: string;
}

export interface Reminder {
  id: string;
  invoice_id?: string;
  kind: ReminderKind;
  sent_to_email: string;
  created_at: string;
}

export interface AgingBucket {
  label: string;
  count: number;
  total: number;
}

export interface AgingReport {
  as_of: string;
  buckets: AgingBucket[];
  total_outstanding: number;
}

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: "Draft",
  ISSUED: "Issued",
  SENT: "Sent",
  VIEWED: "Viewed",
  PARTIALLY_PAID: "Partially Paid",
  PAID: "Paid",
  OVERDUE: "Overdue",
  VOID: "Void",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CHECK: "Check",
  ACH: "ACH",
  CARD: "Card",
  WIRE: "Wire",
  OTHER: "Other",
};

export const REMINDER_KIND_LABELS: Record<ReminderKind, string> = {
  DUE_SOON: "Due soon",
  OVERDUE: "Overdue",
};
